//! NEVER IMPORT FROM HERE. Except in Selector.tsx
// Use the Apollo Client and Redux Store from @/tools.ts

/* --- Application Providers --- */
// allow to customize the link, see https://github.com/apollographql/apollo-link/tree/master/packages/apollo-link-http
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  type NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { createBrowserRouter, createRoutesFromElements } from "react-router-dom";

import WebApiClient from "@/api/WebApiClient.ts";
import { getRoutes } from "@/App.tsx";
import { storage } from "@/app-utils/proxy-storage.ts";
import { urls } from "@/modules/app/utils/urls.ts";
import { notify } from "@/modules/notifications/notify.tsx";
import { logApplicationError, showErrorPage } from "@/redux/modules/errors.ts";

import { unauth_logout } from "./redux/modules/auth.ts";
import { storeTrace } from "./redux/modules/tracing.ts";
import configureStore from "./redux/store/configure-store.ts";
import {
  AUTH_KEY,
  AUTHENTICATION_EXCEPTION,
  AUTHORISATION_EXCEPTION,
  DISCONNECTION_QUEUE_CONTAINER_ID,
  INTERNAL_SERVER_ERROR,
  LICENSE_SEATS_EXCEPTION,
  MODULE_NOT_ENABLED_EXCEPTION,
  NOT_FOUND_EXCEPTION,
  TOKEN_KEY,
  X_AUTH_KEY,
} from "./settings/settings.ts";

const httpLink = createHttpLink({
  uri: `${urls.api}/graphql`,
  // credentials: 'same-origin'  // ...eventually
});

const middlewareLink = new ApolloLink((operation, forward) => {
  const existingToken = storage[TOKEN_KEY];

  operation.setContext({
    headers: {
      [AUTH_KEY]: `Bearer ${existingToken}`,
      [X_AUTH_KEY]: `Bearer ${existingToken}`,
    },
  });

  // Strip __typename from variables so that the query response objects can be used directly as mutation variables
  if (operation.variables) {
    const omitTypename = (key, value) => (key === "__typename" ? undefined : value);
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
  }

  return forward(operation);
});

const tracingLink: ApolloLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (response.extensions?.tracing) {
      store.dispatch(storeTrace(operation.operationName, response.extensions.tracing) as any);
    }

    return response;
  });
});

const errorLink = onError((errorHandler) => {
  const { graphQLErrors, networkError, operation, forward } = errorHandler;
  const context = operation.getContext();
  if (context.skipErrorLink === true) {
    return forward(operation);
  }
  if (graphQLErrors) {
    // @ts-ignore type comes from the server. @see FrontendTypeError
    graphQLErrors.forEach(({ type, message }) => {
      if (
        type === NOT_FOUND_EXCEPTION ||
        type === INTERNAL_SERVER_ERROR ||
        type === AUTHORISATION_EXCEPTION ||
        type === MODULE_NOT_ENABLED_EXCEPTION ||
        type === LICENSE_SEATS_EXCEPTION
      ) {
        store.dispatch(showErrorPage(type, message));
      } else if (type === AUTHENTICATION_EXCEPTION) {
        store.dispatch(unauth_logout());
      }
    });

    // log the error in the errors listing
    store.dispatch(logApplicationError(errorHandler as unknown as Error));
  }

  if (networkError) {
    notify.error(
      { title: "A Network Error Occurred", children: WebApiClient.NETWORK_ERROR_MESSAGE },
      { containerId: DISCONNECTION_QUEUE_CONTAINER_ID, toastId: "__network__" },
    );
  }
});

// allow e.g. optimistic updates
const cache: InMemoryCache = new InMemoryCache({});

const APOLLO_CLIENT_VAR = "__APOLLO_CLIENT__";
// use with apollo-client
export const apolloClient = new ApolloClient<NormalizedCacheObject>({
  link: from([tracingLink, middlewareLink, errorLink, httpLink]), // use with apollo-client
  // use restore on the cache instead of initialState
  // @ts-ignore
  cache: cache.restore(window[APOLLO_CLIENT_VAR]),
  ssrMode: false,
  connectToDevTools: import.meta.env.DEV,
  defaultOptions: { watchQuery: { fetchPolicy: "no-cache" } },
  // We used to have "cache-and-network, but Apollo was requesting twice. See [SEL9-1209]
});

export const store = configureStore(apolloClient);

export const router = createBrowserRouter(createRoutesFromElements(getRoutes()), { basename: urls.basePath });
